// src/pages/Operator/OperatorDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Pagination,
  Dropdown,
  ButtonGroup,
  InputGroup,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import axios from "../../api/axios";

const OperatorDashboard = () => {
  const [parcels, setParcels] = useState([]);
  const [filteredParcels, setFilteredParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Sélection multiple
  const [selectedParcels, setSelectedParcels] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: "", comment: "" });
  const [updating, setUpdating] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    sender_name: "",
    sender_address: "",
    sender_phone: "",
    recipient_name: "",
    recipient_address: "",
    recipient_phone: "",
    recipient_email: "",
    description: "",
    shipment_type: "air",
    weight_kg: "",
    rate_per_kg: "",
    volume_cbm: "",
    rate_per_cbm: "",
    expected_delivery_date: "",
    // ⭐ CHAMPS MARITIMES
    container_number: "",
    bill_of_lading: "",
    container_type: "",
    shipping_line_name: "",
    loading_port: "",
    loading_port_terminal: "",
    discharging_port: "",
    discharging_port_terminal: "",
    current_vessel_name: "",
    current_voyage_number: "",
    last_vessel_name: "",
    last_voyage_number: "",
    last_location: "",
    atd_origin: "",
    eta_final_destination: "",
    actual_delivery_date: "",
    api_container_status: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    inTransit: 0,
    delivered: 0,
    pending: 0,
    totalRevenue: 0,
    airCount: 0,
    seaCount: 0,
  });

  // ⭐ Récupération des colis
  const fetchParcels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/parcels/?all=true");

      let parcelsData = [];
      if (Array.isArray(response.data)) {
        parcelsData = response.data;
      } else if (response.data && response.data.results) {
        parcelsData = response.data.results;
      }

      parcelsData.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      setParcels(parcelsData);
      setFilteredParcels(parcelsData);

      const totalRevenue = parcelsData.reduce(
        (sum, p) => sum + (parseFloat(p.total_cost) || 0),
        0,
      );

      setStats({
        total: parcelsData.length,
        inTransit: parcelsData.filter(
          (p) =>
            p.current_status === "transit_air" ||
            p.current_status === "transit_sea",
        ).length,
        delivered: parcelsData.filter((p) => p.current_status === "delivered")
          .length,
        pending: parcelsData.filter((p) => p.current_status === "registered")
          .length,
        totalRevenue: totalRevenue,
        airCount: parcelsData.filter((p) => p.shipment_type === "air").length,
        seaCount: parcelsData.filter((p) => p.shipment_type === "sea").length,
      });

      setLastUpdate(new Date());
    } catch (err) {
      console.error("Erreur chargement:", err);
      setError("Impossible de charger les colis");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParcels();
  }, [fetchParcels]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchParcels();
        toast.success("Liste rafraîchie", { icon: "🔄", duration: 2000 });
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchParcels]);

  // Filtrage
  useEffect(() => {
    let filtered = [...parcels];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.tracking_number?.toLowerCase().includes(term) ||
          p.recipient_name?.toLowerCase().includes(term) ||
          p.sender_name?.toLowerCase().includes(term) ||
          p.recipient_email?.toLowerCase().includes(term),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((p) => p.current_status === statusFilter);
    }

    if (typeFilter) {
      filtered = filtered.filter((p) => p.shipment_type === typeFilter);
    }

    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      filtered = filtered.filter((p) => new Date(p.created_at) >= startDate);
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59);
      filtered = filtered.filter((p) => new Date(p.created_at) <= endDate);
    }

    setFilteredParcels(filtered);
    setCurrentPage(1);
    setSelectedParcels([]);
    setSelectAll(false);
  }, [searchTerm, statusFilter, typeFilter, dateRange, parcels]);

  // Gestion sélection
  useEffect(() => {
    if (selectAll) {
      setSelectedParcels(currentItems.map((p) => p.id));
    } else {
      setSelectedParcels([]);
    }
  }, [selectAll, currentPage, itemsPerPage, filteredParcels]);

  const handleSelectParcel = (id) => {
    setSelectedParcels((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
    setSelectAll(false);
  };

  const getStatusBadge = (status, expectedDate) => {
    const config = {
      registered: { text: "📝 Enregistré", variant: "secondary", icon: "📝" },
      transit_air: {
        text: "✈️ En transit aérien",
        variant: "primary",
        icon: "✈️",
      },
      transit_sea: {
        text: "🚢 En transit maritime",
        variant: "primary",
        icon: "🚢",
      },
      arrived: { text: "📦 Arrivé", variant: "warning", icon: "📦" },
      delivering: { text: "🚚 En livraison", variant: "info", icon: "🚚" },
      delivered: { text: "✅ Livré", variant: "success", icon: "✅" },
      incident: { text: "⚠️ Incident", variant: "danger", icon: "⚠️" },
    };

    const badgeConfig = config[status] || {
      text: "Inconnu",
      variant: "secondary",
      icon: "❓",
    };

    const isOverdue =
      expectedDate &&
      new Date(expectedDate) < new Date() &&
      status !== "delivered";

    return (
      <Badge
        bg={isOverdue ? "danger" : badgeConfig.variant}
        className="px-3 py-2"
      >
        {badgeConfig.icon} {badgeConfig.text}
        {isOverdue && <span className="ms-1">🔴</span>}
      </Badge>
    );
  };

  const statusOptions = [
    { value: "", label: "📋 Tous les statuts" },
    { value: "registered", label: "📝 Enregistré" },
    { value: "transit_air", label: "✈️ En transit aérien" },
    { value: "transit_sea", label: "🚢 En transit maritime" },
    { value: "arrived", label: "📦 Arrivé" },
    { value: "delivering", label: "🚚 En livraison" },
    { value: "delivered", label: "✅ Livré" },
    { value: "incident", label: "⚠️ Incident" },
  ];

  const typeOptions = [
    { value: "", label: "📦 Tous les types" },
    { value: "air", label: "✈️ Aérien" },
    { value: "sea", label: "🚢 Maritime" },
  ];

  // Pagination
  const getPageNumbers = () => {
    const totalPages = Math.ceil(filteredParcels.length / itemsPerPage);
    const pages = [];
    const maxPagesToShow = 7;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return { pages, totalPages };
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredParcels.slice(indexOfFirstItem, indexOfLastItem);
  const { pages, totalPages } = getPageNumbers();

  // Export Excel
  const exportToExcel = () => {
    const exportData = filteredParcels.map((p) => ({
      "N° Suivi": p.tracking_number,
      Destinataire: p.recipient_name,
      Téléphone: p.recipient_phone,
      Email: p.recipient_email,
      Expéditeur: p.sender_name,
      Type: p.shipment_type === "air" ? "Aérien" : "Maritime",
      "Poids/Volume":
        p.shipment_type === "air" ? `${p.weight_kg} kg` : `${p.volume_cbm} CBM`,
      "Coût Total": `${parseFloat(p.total_cost || 0).toLocaleString()} FCFA`,
      Statut: getStatusBadge(p.current_status, p.expected_delivery_date).props
        .children,
      "Date création": new Date(p.created_at).toLocaleDateString("fr-FR"),
      "Livraison prévue": p.expected_delivery_date
        ? new Date(p.expected_delivery_date).toLocaleDateString("fr-FR")
        : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Colis");
    XLSX.writeFile(wb, `colis_${new Date().toISOString().slice(0, 19)}.xlsx`);
    toast.success("Export Excel réussi");
  };

  // Actions groupées
  const handleBulkStatusUpdate = async () => {
    if (selectedParcels.length === 0) return;
    setShowBulkStatusModal(true);
  };

  const confirmBulkStatusUpdate = async () => {
    setUpdating(true);
    try {
      const promises = selectedParcels.map((id) =>
        axios.post(`/parcels/${id}/status/`, {
          status: statusUpdate.status,
          comment: `[Action groupée] ${statusUpdate.comment || "Mise à jour groupée"}`,
        }),
      );
      await Promise.all(promises);
      toast.success(`${selectedParcels.length} colis mis à jour`);
      setShowBulkStatusModal(false);
      fetchParcels();
      setSelectedParcels([]);
      setSelectAll(false);
    } catch (err) {
      toast.error("Erreur lors de la mise à jour groupée");
    } finally {
      setUpdating(false);
    }
  };

  // ⭐ MODIFICATION : OUVERTURE DU MODAL AVEC TOUS LES CHAMPS
  const openEditModal = (parcel) => {
    setEditingId(parcel.id);
    setEditFormData({
      sender_name: parcel.sender_name || "",
      sender_address: parcel.sender_address || "",
      sender_phone: parcel.sender_phone || "",
      recipient_name: parcel.recipient_name || "",
      recipient_address: parcel.recipient_address || "",
      recipient_phone: parcel.recipient_phone || "",
      recipient_email: parcel.recipient_email || "",
      description: parcel.description || "",
      shipment_type: parcel.shipment_type || "air",
      weight_kg: parcel.weight_kg || "",
      rate_per_kg: parcel.rate_per_kg || "",
      volume_cbm: parcel.volume_cbm || "",
      rate_per_cbm: parcel.rate_per_cbm || "",
      expected_delivery_date: parcel.expected_delivery_date || "",
      // ⭐ CHAMPS MARITIMES
      container_number: parcel.container_number || "",
      bill_of_lading: parcel.bill_of_lading || "",
      container_type: parcel.container_type || "",
      shipping_line_name: parcel.shipping_line_name || "",
      loading_port: parcel.loading_port || "",
      loading_port_terminal: parcel.loading_port_terminal || "",
      discharging_port: parcel.discharging_port || "",
      discharging_port_terminal: parcel.discharging_port_terminal || "",
      current_vessel_name: parcel.current_vessel_name || "",
      current_voyage_number: parcel.current_voyage_number || "",
      last_vessel_name: parcel.last_vessel_name || "",
      last_voyage_number: parcel.last_voyage_number || "",
      last_location: parcel.last_location || "",
      atd_origin: parcel.atd_origin || "",
      eta_final_destination: parcel.eta_final_destination || "",
      actual_delivery_date: parcel.actual_delivery_date || "",
      api_container_status: parcel.api_container_status || "",
    });
    setShowEditModal(true);
  };

  // ⭐ SAUVEGARDE : ENVOI UNIQUEMENT DES CHAMPS MODIFIÉS
  const saveEdit = async () => {
    setEditLoading(true);
    try {
      const originalParcel = parcels.find((p) => p.id === editingId);

      if (!originalParcel) {
        toast.error("Colis non trouvé");
        setEditLoading(false);
        return;
      }

      const submitData = {};

      // ⭐ Fonction de comparaison robuste
      const hasChanged = (newVal, oldVal) => {
        // Normaliser les valeurs pour comparaison
        const normalize = (val) => {
          if (val === undefined || val === null || val === "") return "";
          if (typeof val === "number") return val;
          return val.toString().trim();
        };
        return normalize(newVal) !== normalize(oldVal);
      };

      // Expéditeur
      if (hasChanged(editFormData.sender_name, originalParcel.sender_name)) {
        submitData.sender_name = editFormData.sender_name;
      }
      if (
        hasChanged(editFormData.sender_address, originalParcel.sender_address)
      ) {
        submitData.sender_address = editFormData.sender_address;
      }
      if (hasChanged(editFormData.sender_phone, originalParcel.sender_phone)) {
        submitData.sender_phone = editFormData.sender_phone;
      }

      // Destinataire
      if (
        hasChanged(editFormData.recipient_name, originalParcel.recipient_name)
      ) {
        submitData.recipient_name = editFormData.recipient_name;
      }
      if (
        hasChanged(
          editFormData.recipient_address,
          originalParcel.recipient_address,
        )
      ) {
        submitData.recipient_address = editFormData.recipient_address;
      }
      if (
        hasChanged(editFormData.recipient_phone, originalParcel.recipient_phone)
      ) {
        submitData.recipient_phone = editFormData.recipient_phone;
      }
      if (
        hasChanged(editFormData.recipient_email, originalParcel.recipient_email)
      ) {
        submitData.recipient_email = editFormData.recipient_email;
      }

      // Description
      if (hasChanged(editFormData.description, originalParcel.description)) {
        submitData.description = editFormData.description;
      }

      // Date de livraison
      if (
        hasChanged(
          editFormData.expected_delivery_date,
          originalParcel.expected_delivery_date,
        )
      ) {
        submitData.expected_delivery_date = editFormData.expected_delivery_date;
      }

      // Type d'expédition
      if (
        hasChanged(editFormData.shipment_type, originalParcel.shipment_type)
      ) {
        submitData.shipment_type = editFormData.shipment_type;
      }

      // Données spécifiques Aérien
      if (editFormData.shipment_type === "air") {
        if (hasChanged(editFormData.weight_kg, originalParcel.weight_kg)) {
          submitData.weight_kg = parseFloat(editFormData.weight_kg) || 0;
        }
        if (hasChanged(editFormData.rate_per_kg, originalParcel.rate_per_kg)) {
          submitData.rate_per_kg = parseFloat(editFormData.rate_per_kg) || 0;
        }
      }

      // Données spécifiques Maritime
      if (editFormData.shipment_type === "sea") {
        if (hasChanged(editFormData.volume_cbm, originalParcel.volume_cbm)) {
          submitData.volume_cbm = parseFloat(editFormData.volume_cbm) || 0;
        }
        if (
          hasChanged(editFormData.rate_per_cbm, originalParcel.rate_per_cbm)
        ) {
          submitData.rate_per_cbm = parseFloat(editFormData.rate_per_cbm) || 0;
        }
      }

      // ⭐ CHAMPS MARITIMES
      if (
        hasChanged(
          editFormData.container_number,
          originalParcel.container_number,
        )
      ) {
        submitData.container_number = editFormData.container_number;
      }
      if (
        hasChanged(editFormData.bill_of_lading, originalParcel.bill_of_lading)
      ) {
        submitData.bill_of_lading = editFormData.bill_of_lading;
      }
      if (
        hasChanged(editFormData.container_type, originalParcel.container_type)
      ) {
        submitData.container_type = editFormData.container_type;
      }
      if (
        hasChanged(
          editFormData.shipping_line_name,
          originalParcel.shipping_line_name,
        )
      ) {
        submitData.shipping_line_name = editFormData.shipping_line_name;
      }
      if (hasChanged(editFormData.loading_port, originalParcel.loading_port)) {
        submitData.loading_port = editFormData.loading_port;
      }
      if (
        hasChanged(
          editFormData.loading_port_terminal,
          originalParcel.loading_port_terminal,
        )
      ) {
        submitData.loading_port_terminal = editFormData.loading_port_terminal;
      }
      if (
        hasChanged(
          editFormData.discharging_port,
          originalParcel.discharging_port,
        )
      ) {
        submitData.discharging_port = editFormData.discharging_port;
      }
      if (
        hasChanged(
          editFormData.discharging_port_terminal,
          originalParcel.discharging_port_terminal,
        )
      ) {
        submitData.discharging_port_terminal =
          editFormData.discharging_port_terminal;
      }
      if (
        hasChanged(
          editFormData.current_vessel_name,
          originalParcel.current_vessel_name,
        )
      ) {
        submitData.current_vessel_name = editFormData.current_vessel_name;
      }
      if (
        hasChanged(
          editFormData.current_voyage_number,
          originalParcel.current_voyage_number,
        )
      ) {
        submitData.current_voyage_number = editFormData.current_voyage_number;
      }
      if (
        hasChanged(
          editFormData.last_vessel_name,
          originalParcel.last_vessel_name,
        )
      ) {
        submitData.last_vessel_name = editFormData.last_vessel_name;
      }
      if (
        hasChanged(
          editFormData.last_voyage_number,
          originalParcel.last_voyage_number,
        )
      ) {
        submitData.last_voyage_number = editFormData.last_voyage_number;
      }
      if (
        hasChanged(editFormData.last_location, originalParcel.last_location)
      ) {
        submitData.last_location = editFormData.last_location;
      }

      // Si aucun changement
      if (Object.keys(submitData).length === 0) {
        toast.info("Aucune modification détectée");
        setShowEditModal(false);
        setEditLoading(false);
        return;
      }


      await axios.patch(`/parcels/${editingId}/`, submitData);

      toast.success("Colis modifié avec succès !");
      setShowEditModal(false);
      fetchParcels();
    } catch (err) {
      console.error("❌ Erreur:", err);
      console.error("❌ Response:", err.response?.data);

      let errorMsg = "Erreur lors de la modification";
      if (err.response?.data) {
        if (typeof err.response.data === "object") {
          const firstError = Object.values(err.response.data)[0];
          errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
        } else {
          errorMsg = err.response.data;
        }
      }
      toast.error(errorMsg);
    } finally {
      setEditLoading(false);
    }
  };

  // Changement statut individuel
  const openStatusModal = (parcel) => {
    setSelectedParcel(parcel);
    setStatusUpdate({ status: parcel.current_status, comment: "" });
    setShowStatusModal(true);
  };

  const updateStatus = async () => {
    if (!selectedParcel) return;
    setUpdating(true);
    try {
      await axios.post(`/parcels/${selectedParcel.id}/status/`, {
        status: statusUpdate.status,
        comment: statusUpdate.comment,
      });
      toast.success(
        `Statut du colis ${selectedParcel.tracking_number} mis à jour !`,
      );
      setShowStatusModal(false);
      fetchParcels();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleShipmentTypeChange = (e) => {
    const newType = e.target.value;
    setEditFormData({
      ...editFormData,
      shipment_type: newType,
      weight_kg: "",
      rate_per_kg: "",
      volume_cbm: "",
      rate_per_cbm: "",
    });
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("fr-FR").format(value);
  };

  // Mémorisation des items affichés
  const displayedItems = useMemo(() => currentItems, [currentItems]);

  if (loading)
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Spinner animation="border" style={{ color: "#C62828" }} />
      </Container>
    );

  return (
    <Container className="py-4" fluid="lg">
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Stats Cards */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center stat-card">
            <Card.Body>
              <div className="display-4 fw-bold text-danger">{stats.total}</div>
              <div className="text-muted">
                <span>📦 Total colis</span>
                <span className="ms-2 badge bg-secondary">
                  {stats.airCount} ✈️ / {stats.seaCount} 🚢
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center stat-card">
            <Card.Body>
              <div className="display-4 fw-bold text-primary">
                {stats.inTransit}
              </div>
              <div className="text-muted">🚚 En transit</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center stat-card">
            <Card.Body>
              <div className="display-4 fw-bold text-success">
                {stats.delivered}
              </div>
              <div className="text-muted">✅ Livrés</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center stat-card bg-warning bg-opacity-10">
            <Card.Body>
              <div className="display-4 fw-bold text-warning">
                {formatNumber(stats.totalRevenue)}
              </div>
              <div className="text-muted">💰 Chiffre d'affaires (FCFA)</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Barre d'actions */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h5 className="mb-0 fw-bold">📋 Gestion des colis</h5>
              <small className="text-muted">
                Dernière mise à jour: {lastUpdate.toLocaleTimeString()}
                <Button
                  variant="link"
                  size="sm"
                  onClick={fetchParcels}
                  className="p-0 ms-2"
                >
                  🔄
                </Button>
              </small>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant={autoRefresh ? "success" : "outline-secondary"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? "⏸ Auto-refresh ON" : "▶ Auto-refresh OFF"}
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                onClick={exportToExcel}
              >
                📊 Exporter Excel
              </Button>
              <Button
                variant="outline-success"
                as={Link}
                to="/operator/parcels/import"
              >
                📥 Import Excel
              </Button>
              <Button variant="danger" as={Link} to="/operator/parcels/create">
                + Nouveau colis
              </Button>
            </div>
          </div>

          <Row className="g-2">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="N° suivi, destinataire, expéditeur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm("")}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button
                variant="outline-secondary"
                onClick={() => setShowFilters(!showFilters)}
                className="w-100"
              >
                {showFilters ? "▲ Filtres avancés" : "▼ Filtres avancés"}
              </Button>
            </Col>
          </Row>

          {showFilters && (
            <Row className="mt-3 g-2">
              <Col md={5}>
                <Form.Label className="small text-muted">
                  Date de création (début)
                </Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                />
              </Col>
              <Col md={5}>
                <Form.Label className="small text-muted">
                  Date de création (fin)
                </Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                />
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setDateRange({ start: "", end: "" })}
                  className="w-100"
                >
                  Réinitialiser
                </Button>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Actions groupées */}
      {selectedParcels.length > 0 && (
        <div className="mb-3 p-2 bg-light rounded d-flex justify-content-between align-items-center">
          <span>
            <Badge bg="primary" className="me-2">
              {selectedParcels.length}
            </Badge>
            colis sélectionnés
          </span>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleBulkStatusUpdate}
            >
              📊 Changer statut
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setSelectedParcels([])}
            >
              Annuler sélection
            </Button>
          </div>
        </div>
      )}

      {/* Tableau des colis */}
      <Card className="border-0 shadow-lg rounded-4">
        <Card.Body className="p-0">
          {filteredParcels.length === 0 ? (
            <div className="text-center py-5">
              <div className="display-1 mb-3">📭</div>
              <p className="text-muted">Aucun colis trouvé</p>
              <div className="d-flex gap-3 justify-content-center">
                <Button
                  variant="outline-success"
                  as={Link}
                  to="/operator/parcels/import"
                >
                  📥 Importer un fichier Excel
                </Button>
                <Button
                  variant="danger"
                  as={Link}
                  to="/operator/parcels/create"
                >
                  + Créer un colis manuellement
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "40px" }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectAll && currentItems.length > 0}
                          onChange={() => setSelectAll(!selectAll)}
                        />
                      </th>
                      <th>N° Suivi</th>
                      <th>Destinataire</th>
                      <th>Expéditeur</th>
                      <th>Statut</th>
                      <th>Type</th>
                      <th>Poids/Volume</th>
                      <th>Coût</th>
                      <th>Date</th>
                      <th>Livraison prévue</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedItems.map((parcel) => {
                      const isSelected = selectedParcels.includes(parcel.id);
                      return (
                        <tr
                          key={parcel.id}
                          className={isSelected ? "table-primary" : ""}
                        >
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectParcel(parcel.id)}
                            />
                          </td>
                          <td>
                            <Link
                              to={`/tracking/${parcel.tracking_number}`}
                              className="font-monospace fw-bold text-decoration-none"
                            >
                              {parcel.tracking_number}
                            </Link>
                          </td>
                          <td>
                            <div className="fw-semibold">
                              {parcel.recipient_name}
                            </div>
                            {parcel.recipient_phone && (
                              <small className="text-muted">
                                {parcel.recipient_phone}
                              </small>
                            )}
                          </td>
                          <td>{parcel.sender_name}</td>
                          <td>
                            {getStatusBadge(
                              parcel.current_status,
                              parcel.expected_delivery_date,
                            )}
                          </td>
                          <td>
                            <Badge
                              bg={
                                parcel.shipment_type === "air"
                                  ? "info"
                                  : "primary"
                              }
                            >
                              {parcel.shipment_type === "air"
                                ? "✈️ Aérien"
                                : "🚢 Maritime"}
                            </Badge>
                          </td>
                          <td>
                            {parcel.shipment_type === "air"
                              ? `${parcel.weight_kg} kg`
                              : `${parcel.volume_cbm} CBM`}
                          </td>
                          <td className="fw-bold text-success">
                            {parcel.total_cost
                              ? `${formatNumber(parcel.total_cost)} FCFA`
                              : "-"}
                          </td>
                          <td>
                            {new Date(parcel.created_at).toLocaleDateString(
                              "fr-FR",
                            )}
                          </td>
                          <td>
                            {parcel.expected_delivery_date ? (
                              <span
                                className={
                                  new Date(parcel.expected_delivery_date) <
                                    new Date() &&
                                  parcel.current_status !== "delivered"
                                    ? "text-danger fw-bold"
                                    : "text-success"
                                }
                              >
                                {new Date(
                                  parcel.expected_delivery_date,
                                ).toLocaleDateString("fr-FR")}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            <Dropdown as={ButtonGroup} size="sm">
                              <Button
                                variant="outline-primary"
                                onClick={() => openEditModal(parcel)}
                              >
                                ✏️
                              </Button>
                              <Dropdown.Toggle
                                split
                                variant="outline-primary"
                              />
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() => openEditModal(parcel)}
                                >
                                  ✏️ Modifier
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => openStatusModal(parcel)}
                                >
                                  📊 Changer statut
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  as={Link}
                                  to={`/tracking/${parcel.tracking_number}`}
                                  target="_blank"
                                >
                                  🔍 Suivre le colis
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-3 px-3 border-top">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Lignes par page :</span>
                  <Form.Select
                    size="sm"
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    style={{ width: "80px" }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </div>

                <div className="text-muted small">
                  {indexOfFirstItem + 1} -{" "}
                  {Math.min(indexOfLastItem, filteredParcels.length)} sur{" "}
                  {filteredParcels.length} colis
                </div>

                {totalPages > 1 && (
                  <Pagination size="sm" className="mb-0">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    />

                    {pages.map((page, idx) =>
                      page === "..." ? (
                        <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                      ) : (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      ),
                    )}

                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal modification */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>✏️ Modifier le colis</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <h6 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              📤 Expéditeur
            </h6>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Nom</Form.Label>
                <Form.Control
                  type="text"
                  name="sender_name"
                  value={editFormData.sender_name}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Téléphone</Form.Label>
                <Form.Control
                  type="text"
                  name="sender_phone"
                  value={editFormData.sender_phone}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={12} className="mt-2">
                <Form.Label>Adresse</Form.Label>
                <Form.Control
                  type="text"
                  name="sender_address"
                  value={editFormData.sender_address}
                  onChange={handleEditChange}
                />
              </Col>
            </Row>

            <h6 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              📥 Destinataire
            </h6>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Nom</Form.Label>
                <Form.Control
                  type="text"
                  name="recipient_name"
                  value={editFormData.recipient_name}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Téléphone</Form.Label>
                <Form.Control
                  type="text"
                  name="recipient_phone"
                  value={editFormData.recipient_phone}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={12} className="mt-2">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="recipient_email"
                  value={editFormData.recipient_email}
                  onChange={handleEditChange}
                />
              </Col>
              <Col md={12} className="mt-2">
                <Form.Label>Adresse</Form.Label>
                <Form.Control
                  type="text"
                  name="recipient_address"
                  value={editFormData.recipient_address}
                  onChange={handleEditChange}
                />
              </Col>
            </Row>

            <h6 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              📦 Description
            </h6>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={editFormData.description}
                onChange={handleEditChange}
              />
            </Form.Group>

            <h6 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              🚚 Transport
            </h6>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Type d'expédition</Form.Label>
                <Form.Select
                  name="shipment_type"
                  value={editFormData.shipment_type}
                  onChange={handleShipmentTypeChange}
                >
                  <option value="air">✈️ Aérien</option>
                  <option value="sea">🚢 Maritime</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Date livraison prévue</Form.Label>
                <Form.Control
                  type="date"
                  name="expected_delivery_date"
                  value={editFormData.expected_delivery_date}
                  onChange={handleEditChange}
                />
              </Col>
            </Row>

            {editFormData.shipment_type === "air" ? (
              <Row>
                <Col md={6}>
                  <Form.Label>Poids (kg)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="weight_kg"
                    value={editFormData.weight_kg}
                    onChange={handleEditChange}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Tarif au kg (FCFA)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="rate_per_kg"
                    value={editFormData.rate_per_kg}
                    onChange={handleEditChange}
                  />
                </Col>
              </Row>
            ) : (
              <Row>
                <Col md={6}>
                  <Form.Label>Volume (CBM)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="volume_cbm"
                    value={editFormData.volume_cbm}
                    onChange={handleEditChange}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Tarif au CBM (FCFA)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="rate_per_cbm"
                    value={editFormData.rate_per_cbm}
                    onChange={handleEditChange}
                  />
                </Col>
              </Row>
            )}

            {/* ⭐ SECTION MARITIME - AFFICHÉE UNIQUEMENT POUR LE MARITIME */}
            {editFormData.shipment_type === "sea" && (
              <>
                <h6 className="fw-bold mb-3 mt-4" style={{ color: "#C62828" }}>
                  🚢 Informations maritimes
                </h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>Numéro de conteneur</Form.Label>
                    <Form.Control
                      type="text"
                      name="container_number"
                      value={editFormData.container_number}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Packing List No.</Form.Label>
                    <Form.Control
                      type="text"
                      name="bill_of_lading"
                      value={editFormData.bill_of_lading}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Compagnie maritime</Form.Label>
                    <Form.Control
                      type="text"
                      name="shipping_line_name"
                      value={editFormData.shipping_line_name}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Type de conteneur</Form.Label>
                    <Form.Control
                      type="text"
                      name="container_type"
                      value={editFormData.container_type}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Port de chargement</Form.Label>
                    <Form.Control
                      type="text"
                      name="loading_port"
                      value={editFormData.loading_port}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Terminal de chargement</Form.Label>
                    <Form.Control
                      type="text"
                      name="loading_port_terminal"
                      value={editFormData.loading_port_terminal}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Port de déchargement</Form.Label>
                    <Form.Control
                      type="text"
                      name="discharging_port"
                      value={editFormData.discharging_port}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Terminal de déchargement</Form.Label>
                    <Form.Control
                      type="text"
                      name="discharging_port_terminal"
                      value={editFormData.discharging_port_terminal}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Navire actuel</Form.Label>
                    <Form.Control
                      type="text"
                      name="current_vessel_name"
                      value={editFormData.current_vessel_name}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>N° Voyage</Form.Label>
                    <Form.Control
                      type="text"
                      name="current_voyage_number"
                      value={editFormData.current_voyage_number}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Dernier navire</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_vessel_name"
                      value={editFormData.last_vessel_name}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Label>Dernier N° Voyage</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_voyage_number"
                      value={editFormData.last_voyage_number}
                      onChange={handleEditChange}
                    />
                  </Col>
                  <Col md={12} className="mt-2">
                    <Form.Label>Dernière localisation</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_location"
                      value={editFormData.last_location}
                      onChange={handleEditChange}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={saveEdit} disabled={editLoading}>
            {editLoading ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : null}
            {editLoading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal changement statut individuel */}
      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>📊 Changer le statut</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedParcel && (
            <>
              <p>
                <strong>Colis :</strong> {selectedParcel.tracking_number}
              </p>
              <p>
                <strong>Destinataire :</strong> {selectedParcel.recipient_name}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Nouveau statut</Form.Label>
                <Form.Select
                  value={statusUpdate.status}
                  onChange={(e) =>
                    setStatusUpdate({ ...statusUpdate, status: e.target.value })
                  }
                >
                  {statusOptions
                    .filter((opt) => opt.value)
                    .map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Commentaire (optionnel)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={statusUpdate.comment}
                  onChange={(e) =>
                    setStatusUpdate({
                      ...statusUpdate,
                      comment: e.target.value,
                    })
                  }
                  placeholder="Ajoutez un commentaire..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={updateStatus} disabled={updating}>
            {updating ? "Mise à jour..." : "Mettre à jour"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal changement statut groupé */}
      <Modal
        show={showBulkStatusModal}
        onHide={() => setShowBulkStatusModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>📊 Mise à jour groupée</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{selectedParcels.length}</strong> colis sélectionnés
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Nouveau statut</Form.Label>
            <Form.Select
              value={statusUpdate.status}
              onChange={(e) =>
                setStatusUpdate({ ...statusUpdate, status: e.target.value })
              }
            >
              {statusOptions
                .filter((opt) => opt.value)
                .map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Commentaire (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={statusUpdate.comment}
              onChange={(e) =>
                setStatusUpdate({ ...statusUpdate, comment: e.target.value })
              }
              placeholder="Ajoutez un commentaire commun..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBulkStatusModal(false)}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={confirmBulkStatusUpdate}
            disabled={updating}
          >
            {updating
              ? "Mise à jour..."
              : `Mettre à jour (${selectedParcels.length} colis)`}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OperatorDashboard;
